import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ThumbsUp, MessageCircle, Users, ExternalLink, Star, Heart, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from "@/components/ui/badge";
import { openExternal } from "@/lib/utils";
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SocialPost } from '@/types/team-posts';
import { usePostEngagers } from '@/hooks/useSocialEngagers';

interface PostEngagementModalProps {
  post: SocialPost | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CategoryBadge({ category }: { category: string | null }) {
  const config: Record<string, { label: string; className: string }> = {
    thought_leaders: { label: 'Thought Leader', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100' },
    your_brand: { label: 'Your Brand', className: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-100' },
    competitors: { label: 'Competitor', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100' },
    event_tracking: { label: 'Event', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' },
    technology_providers: { label: 'Tech Provider', className: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-100' },
  };
  
  const c = config[category || ''] || { label: category || 'Unknown', className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100' };
  
  return <Badge className={`text-xs ${c.className}`}>{c.label}</Badge>;
}

export function PostEngagementModal({ post, open, onOpenChange }: PostEngagementModalProps) {
  const [showFullContent, setShowFullContent] = useState(false);

  const postUrl = post?.post_url;
  const { data: engagers = [], isLoading: engagersLoading } = usePostEngagers(postUrl || null);

  useMemo(() => {
    setShowFullContent(false);
  }, [postUrl]);

  if (!post) return null;

  const displayName = post.poster_name || post.post_author || 'Unknown';
  const initials = displayName.substring(0, 2).toUpperCase();
  const formattedDate = post.post_date 
    ? format(new Date(post.post_date), 'MMMM d, yyyy')
    : 'Unknown date';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="pb-4 border-b px-6 pt-6 flex-shrink-0">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="text-lg bg-primary/10 text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">{displayName}</span>
                  {post.category && <CategoryBadge category={post.category} />}
                </div>
                <p className="text-sm text-muted-foreground font-normal">
                  {formattedDate}
                </p>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-auto px-6">
          <div className="space-y-6 py-4">
            {/* Post Content */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="relative">
                <p 
                  className={`text-sm whitespace-pre-wrap leading-relaxed ${!showFullContent ? 'line-clamp-3' : ''}`}
                >
                  {post.full_content || post.content_preview}
                </p>
                {(post.full_content || post.content_preview)?.split('\n').length! > 3 && (
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="p-0 h-auto text-primary mt-1"
                    onClick={() => setShowFullContent(!showFullContent)}
                  >
                    {showFullContent ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </div>
              {post.post_url && (
                <div className="mt-4 pt-4 border-t border-border/50">
                  <Button variant="outline" size="sm" asChild>
                    <a href={post.post_url.startsWith('http') ? post.post_url : `https://${post.post_url}`} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View on LinkedIn
                    </a>
                  </Button>
                </div>
              )}
            </div>

            {/* Engagement Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 bg-pink-50 dark:bg-pink-950/30 rounded-lg">
                <Heart className="h-5 w-5 text-pink-600" />
                <div>
                  <p className="text-lg font-bold">{post.likes}</p>
                  <p className="text-xs text-muted-foreground">Likes</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                <MessageCircle className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-lg font-bold">{post.comments}</p>
                  <p className="text-xs text-muted-foreground">Comments</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <Star className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-lg font-bold">{post.top_account_engagers}</p>
                  <p className="text-xs text-muted-foreground">Top Accounts</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-lg font-bold">{post.unique_engagers}</p>
                  <p className="text-xs text-muted-foreground">Total Engagers</p>
                </div>
              </div>
            </div>

            {/* Engagers Table */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Engagers ({engagers.length})
              </h3>
              {engagersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : engagers.length === 0 ? (
                <div className="text-center text-muted-foreground py-6 border rounded-lg">
                  <p className="text-sm">No engager data available for this post</p>
                </div>
              ) : (
                <div className="border rounded-lg max-h-[300px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead className="w-[80px]">Type</TableHead>
                        <TableHead className="w-[60px] text-center">Match</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {engagers.map((eng) => (
                        <TableRow key={eng.engagement_id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-7 w-7">
                                <AvatarFallback className="text-[10px] bg-muted">
                                  {(eng.engager_name || '?').substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                {eng.engager_linkedin_url ? (
                                  <button
                                    type="button"
                                    className="text-sm font-medium hover:underline text-primary truncate block text-left"
                                    onClick={(e) => { e.stopPropagation(); openExternal(eng.engager_linkedin_url); }}
                                  >
                                    {eng.engager_name}
                                  </button>
                                ) : (
                                  <span className="text-sm font-medium truncate block">{eng.engager_name}</span>
                                )}
                                {eng.engager_company && (
                                  <span className="text-xs text-muted-foreground truncate block">{eng.engager_company}</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground line-clamp-1">
                              {eng.engager_title && eng.engager_title !== '--' ? eng.engager_title : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-[10px] capitalize">
                              {eng.engagement_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {eng.is_top_account ? (
                              <Star className="h-4 w-4 text-amber-500 mx-auto" />
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
        <div className="h-4 flex-shrink-0" />
      </DialogContent>
    </Dialog>
  );
}
